/**
 * Public client configuration, resolved at build time.
 *
 * These values are baked into the bundle by Vite, so they are public by
 * definition. Only variables named in `envPrefix` (vite.config.ts) reach here.
 *
 * `contactEmail` and `mailingAddress` are intentionally optional. An unset
 * value renders a loud "not set" marker in the footer rather than a blank line,
 * because a site that goes public without a contact address should fail review
 * rather than look finished.
 */

/**
 * Canonical production origin. The fallback is the real site rather than a
 * localhost URL on purpose: canonical tags and share URLs built from a
 * machine-local address are worse than ones built from the production origin.
 */
const DEFAULT_SITE_URL = 'https://myherdbook.com';

function present(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? undefined : trimmed;
}

function normalizeBaseUrl(value: string): string {
  return value.replace(/\/+$/, '');
}

export interface PublicConfig {
  /** Origin used to build canonical and Open Graph URLs. Never ends in a slash. */
  siteUrl: string;
  /** Public contact address shown in the footer. Undefined until supplied. */
  contactEmail?: string;
  /** Physical mailing address shown in the footer. Undefined until supplied. */
  mailingAddress?: string;
}

export const publicConfig: PublicConfig = {
  siteUrl: normalizeBaseUrl(present(import.meta.env.PUBLIC_SITE_URL) ?? DEFAULT_SITE_URL),
  contactEmail: present(import.meta.env.CONTACT_EMAIL),
  mailingAddress: present(import.meta.env.MAILING_ADDRESS),
};

/** Joins a site-root-relative path onto the canonical origin. */
export function absoluteUrl(path: string): string {
  return `${publicConfig.siteUrl}${path.startsWith('/') ? path : `/${path}`}`;
}
